import { CalendarClock, Download, Mail, Plus, Upload } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router'
import { EmptyState } from '@/components/EmptyState'
import { Button } from '@/components/ui/button'
import { contactColumns } from './contactColumns'
import { ContactCardList } from './ContactCardList'
import { ContactMailDialog } from './ContactMailDialog'
import { FollowUpDialog } from './FollowUpDialog'
import { RecordListPage, type RecordListConfig } from './list/RecordListPage'
import { NewContactDialog } from './NewContactDialog'
import type { ContactRow } from './query/types'

const noun = (count: number): string => (count === 1 ? '1 contact' : `${count} contacten`)

const SORT_OPTIONS = [
  { value: 'name', label: 'Naam (A–Z)' },
  { value: '-last_activity_at', label: 'Laatst gewijzigd (nieuwste eerst)' },
  { value: 'last_contact_at', label: 'Laatste contact (oudste eerst)' },
  { value: 'next_action_on', label: 'Volgende actie' },
] as const

/**
 * Contacten (spec-44, rebuilt in spec-47 and spec-63 E2): the one list page with the views, filters,
 * columns, bulk and export of the register of contacts, plus a "Nieuw contact" dialog and a mail and a
 * follow-up dialog per row.
 */
export function ContactsPage() {
  const navigate = useNavigate()
  const [newOpen, setNewOpen] = useState(false)
  const [followUpFor, setFollowUpFor] = useState<ContactRow | null>(null)
  const [mailFor, setMailFor] = useState<ContactRow | null>(null)

  const config: RecordListConfig<ContactRow> = {
    object: 'contacten',
    title: 'Contacten',
    subtitle: 'Alle relaties van VONK op één plek',
    searchPlaceholder: 'Zoek op naam, e-mail, telefoon of organisatie…',
    searchLabel: 'Zoek contacten',
    noun,
    rowKey: (row) => row.person_id,
    rowLabel: (row) => row.full_name,
    columns: contactColumns(),
    cardList: (rows, sort) => <ContactCardList rows={rows} sort={sort} />,
    sortOptions: SORT_OPTIONS,
    saveAsList: true,
    headerActions: (list, { exporting, exportRows }) => (
      <>
        <Button type="button" variant="outline" onClick={() => void exportRows()} disabled={exporting || !list.query}>
          <Download />
          {exporting ? 'Bezig…' : 'Exporteren'}
        </Button>
        <Button type="button" variant="outline" onClick={() => navigate('/relaties/importeren?modus=contacten')}>
          <Upload />
          Importeren
        </Button>
        <Button type="button" onClick={() => setNewOpen(true)}>
          <Plus />
          Nieuw contact
        </Button>
      </>
    ),
    rowActions: (row) => (
      <>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={() => setMailFor(row)}
          disabled={!row.email}
          aria-label={`E-mail sturen aan ${row.full_name}`}
          title={row.email ? 'E-mail sturen' : 'Contact heeft geen e-mailadres'}
        >
          <Mail />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          onClick={() => setFollowUpFor(row)}
          aria-label={`Opvolgen ${row.full_name}`}
          title="Opvolgtaak plannen"
        >
          <CalendarClock />
        </Button>
      </>
    ),
    empty: (
      <EmptyState
        title="Nog geen contacten."
        description="Maak het eerste contact aan."
        action={
          <Button type="button" onClick={() => setNewOpen(true)}>
            <Plus />
            Nieuw contact
          </Button>
        }
      />
    ),
    exportFilename: 'contacten.xlsx',
    dialogs: (list) => (
      <>
        {followUpFor ? (
          <FollowUpDialog
            open
            onOpenChange={(open) => {
              if (!open) setFollowUpFor(null)
            }}
            personId={followUpFor.person_id}
            personName={followUpFor.full_name}
            onCreated={list.reload}
          />
        ) : null}
        {mailFor ? (
          <ContactMailDialog
            open
            onOpenChange={(open) => {
              if (!open) setMailFor(null)
            }}
            contact={{
              person_id: mailFor.person_id,
              full_name: mailFor.full_name,
              email: mailFor.email,
              organisation: mailFor.primary_organisation?.name ?? null,
            }}
            onSent={list.reload}
          />
        ) : null}
        <NewContactDialog open={newOpen} onOpenChange={setNewOpen} onCreated={(personId) => void navigate(`/relaties/contacten/${personId}`)} />
      </>
    ),
  }

  return <RecordListPage config={config} />
}
